import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Computes alternating cyber checkerboard corner styles for grid cards
 *
 * @param {number} index - Item index in the grid
 * @param {number} total - Total items in the grid
 * @param {number} [cols=1] - Number of active columns
 * @param {string} [radius='1.5rem'] - Corner radius
 * @returns {React.CSSProperties}
 */
export function getCornerStyle(index, total, cols = 1, radius = '1.5rem') {
    if (!total || total <= 0) return {};
    const safeCols = Math.max(1, (typeof cols === 'number' && cols > 0) ? cols : 1);
    const row = Math.floor(index / safeCols);
    const col = index % safeCols;

    // Alternates both horizontally across columns and vertically across rows (checkerboard pattern)
    const isPatternA = (row + col) % 2 === 0;

    if (isPatternA) {
        return {
            borderTopLeftRadius: '0px',
            borderTopRightRadius: radius,
            borderBottomLeftRadius: radius,
            borderBottomRightRadius: '0px',
        };
    } else {
        return {
            borderTopLeftRadius: radius,
            borderTopRightRadius: '0px',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: radius,
        };
    }
}

/**
 * Custom hook to dynamically calculate grid columns and provide
 * alternating checkerboard corner styles for grid cards.
 *
 * @param {Object} [options]
 * @param {string} [options.viewMode] - 'grid' | 'list' | 'vertical' | 'horizontal'
 * @param {number} [options.defaultCols=4] - Fallback columns for largest breakpoint (4 or 5)
 * @param {string} [options.radius='1.5rem'] - Corner radius
 */
export function useGridCorners({ viewMode, defaultCols = 4, radius = '1.5rem' } = {}) {
    const [gridCols, setGridCols] = useState(() => {
        if (typeof window === 'undefined') return 1;
        const w = window.innerWidth;
        if (defaultCols >= 5) {
            if (w >= 1536) return 5;
            if (w >= 1024) return 4;
            if (w >= 768) return 3;
            if (w >= 500) return 2;
            return 1;
        }
        if (w >= 1440 || (w >= 1024 && defaultCols === 4)) return 4;
        if (w >= 768) return 3;
        if (w >= 500) return 2;
        return 1;
    });

    const gridElementRef = useRef(null);
    const resizeObserverRef = useRef(null);

    const updateGridCols = useCallback((node) => {
        const targetNode = node || gridElementRef.current;
        if (targetNode) {
            const templateCols = window.getComputedStyle(targetNode).gridTemplateColumns;
            if (templateCols && templateCols !== 'none') {
                const cols = templateCols.split(' ').filter(Boolean).length;
                if (cols > 0) {
                    setGridCols(cols);
                    return;
                }
            }
        }
        const w = window.innerWidth;
        if (viewMode === 'list' || viewMode === 'horizontal') {
            setGridCols(w >= 1024 ? 2 : 1);
        } else if (defaultCols >= 5) {
            if (w >= 1536) setGridCols(5);
            else if (w >= 1024) setGridCols(4);
            else if (w >= 768) setGridCols(3);
            else if (w >= 500) setGridCols(2);
            else setGridCols(1);
        } else {
            if (w >= 1440 || w >= 1024) setGridCols(4);
            else if (w >= 768) setGridCols(3);
            else if (w >= 500) setGridCols(2);
            else setGridCols(1);
        }
    }, [viewMode, defaultCols]);

    const setGridRef = useCallback((node) => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
        }
        gridElementRef.current = node;
        if (node) {
            updateGridCols(node);
            if (typeof ResizeObserver !== 'undefined') {
                const ro = new ResizeObserver(() => updateGridCols(node));
                ro.observe(node);
                resizeObserverRef.current = ro;
            }
        }
    }, [updateGridCols]);

    useEffect(() => {
        const handleWindowResize = () => updateGridCols();
        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [updateGridCols]);

    const getBoundCornerStyle = useCallback((index, total, cols = gridCols) => {
        return getCornerStyle(index, total, cols || gridCols, radius);
    }, [gridCols, radius]);

    return {
        gridCols,
        setGridRef,
        getCornerStyle: getBoundCornerStyle,
        gridElementRef
    };
}

export default useGridCorners;
